import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { Globe, CreditCard, Smartphone, ShieldCheck, ArrowUpRight, X, Check, Layers } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const ICONS = { Globe, CreditCard, Smartphone, ShieldCheck, Layers };
const iconFor = (name) => ICONS[name] || Layers;

export const Services = () => {
  const s = useContent().services;
  const [selected, setSelected] = useState(null);
  const SelIcon = selected ? iconFor(selected.icon) : null;

  return (
    <section id="services" data-testid="services-section" className="bg-surface py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-4">{s.eyebrow}</p>
          <h2 className="font-jp text-3xl sm:text-5xl font-bold tracking-tight chrome-text max-w-2xl leading-tight whitespace-pre-line">
            {s.heading}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {s.cards.map((card, i) => {
            const Icon = iconFor(card.icon);
            const span = i % 4 === 0 || i % 4 === 3 ? "md:col-span-8" : "md:col-span-4";
            const tall = card.image ? "min-h-[380px]" : "min-h-[240px]";
            return (
              <Reveal key={card.id || i} delay={i * 0.06} className={span}>
                <button
                  type="button"
                  onClick={() => setSelected(card)}
                  data-testid={`service-card-${i}`}
                  className={`hud group relative w-full text-left h-full overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:glow-cyan focus:outline-none ${tall}`}
                >
                  <span className="hud-tl" />
                  <span className="hud-br" />
                  {card.image && (
                    <>
                      <img src={card.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale transition-all duration-700 group-hover:opacity-40 group-hover:grayscale-0 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                    </>
                  )}
                  <div className="relative flex h-full flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <Icon className="text-gold" size={28} strokeWidth={1.4} />
                      <ArrowUpRight className="text-white/30 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1" size={22} />
                    </div>
                    <div>
                      <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold mb-3">{card.tag}</p>
                      <h3 className="font-jp text-2xl font-bold text-white mb-3">{card.title}</h3>
                      <p className="text-sm font-light leading-relaxed text-white/60 max-w-md">{card.body}</p>
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            data-testid="service-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-6"
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="hud relative w-full max-w-2xl rounded-2xl border border-gold/30 bg-surface p-8 md:p-12 glow-cyan"
            >
              <span className="hud-tl" />
              <span className="hud-br" />
              <button onClick={() => setSelected(null)} data-testid="service-modal-close" className="absolute top-6 right-6 text-white/50 hover:text-gold transition-colors">
                <X size={22} />
              </button>
              <div className="grid place-items-center h-14 w-14 rounded-xl border border-gold/40 text-gold mb-6">
                {SelIcon && <SelIcon size={26} strokeWidth={1.4} />}
              </div>
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold mb-2">{selected.tag}</p>
              <h3 className="font-jp text-3xl md:text-4xl font-bold tracking-tight chrome-text leading-tight">{selected.title}</h3>
              <div className="hairline my-8" />
              <p className="text-base font-light leading-relaxed text-white/75">{selected.detail}</p>
              {selected.points?.length > 0 && (
                <ul className="mt-8 space-y-3">
                  {selected.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-white/80">
                      <Check size={16} className="mt-0.5 text-gold shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
